import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { listCampaigns, createCampaign } from '@/services/campaigns';
import { updateCreative } from '@/services/creatives';
import type { Campaign } from '@/types/api';

interface AssignCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  creativeId: number | null;
  creativeName: string;
  onAssigned?: () => void;
}

export default function AssignCampaignModal({
  isOpen,
  onClose,
  creativeId,
  creativeName,
  onAssigned,
}: AssignCampaignModalProps) {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [assigningId, setAssigningId] = useState<number | null>(null);
  const [error, setError] = useState('');

  // New campaign inline
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    listCampaigns(0, 100)
      .then((list) => setCampaigns(list.filter((c) => c.id != null)))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load campaigns'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setShowCreate(false);
      setNewName('');
      return;
    }
    setError('');
    load();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, load, onClose]);

  async function handleAssign(campaignId: number) {
    if (creativeId == null) return;
    setAssigningId(campaignId);
    try {
      await updateCreative(creativeId, { assigned_campaign_ids: [campaignId] });
      onAssigned?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to assign');
    } finally {
      setAssigningId(null);
    }
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const c = await createCampaign({ name: newName.trim() });
      load();
      setShowCreate(false);
      setNewName('');
      if (c.id != null) handleAssign(c.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create campaign');
    } finally {
      setCreating(false);
    }
  }

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-on-surface/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg bg-surface-container-lowest rounded-2xl shadow-2xl overflow-hidden border border-outline-variant/10">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-outline-variant/10">
          <div>
            <h2 className="font-headline font-bold text-on-surface text-lg">Add to Campaign</h2>
            <p className="text-sm text-on-surface-variant mt-0.5 truncate">{creativeName}</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-surface-container text-on-surface-variant hover:text-on-surface transition-colors cursor-pointer"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-3 max-h-[60vh] overflow-y-auto">
          {error && (
            <div className="p-3 rounded-lg bg-error-container text-on-error-container text-sm">
              {error}
            </div>
          )}

          {/* Create new inline */}
          {showCreate ? (
            <form onSubmit={handleCreate} className="flex gap-2 mb-4">
              <input
                autoFocus
                required
                className="flex-1 px-3 py-2.5 bg-surface-container-low text-sm text-on-surface border border-primary/40 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="New campaign name…"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <button
                type="submit"
                disabled={creating || !newName.trim()}
                className="px-4 py-2 bg-primary text-on-primary rounded-lg font-bold text-sm disabled:opacity-50 cursor-pointer hover:bg-primary-dim transition-all"
              >
                {creating ? '…' : 'Create'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreate(false);
                  setNewName('');
                }}
                className="px-3 py-2 text-on-surface-variant hover:text-on-surface cursor-pointer"
              >
                Cancel
              </button>
            </form>
          ) : (
            <button
              onClick={() => setShowCreate(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 mb-2 rounded-xl border border-dashed border-outline-variant/40 text-primary text-sm font-bold hover:bg-primary-container/30 transition-colors cursor-pointer"
            >
              <span className="material-symbols-outlined text-lg">add</span>
              New Campaign
            </button>
          )}

          {loading ? (
            <div className="flex justify-center py-10">
              <span className="material-symbols-outlined animate-spin text-primary text-4xl">
                progress_activity
              </span>
            </div>
          ) : campaigns.length === 0 ? (
            <p className="text-on-surface-variant text-sm py-4 text-center">
              No campaigns yet. Create one above.
            </p>
          ) : (
            <ul className="space-y-1.5">
              {campaigns.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    disabled={assigningId != null}
                    onClick={() => handleAssign(c.id!)}
                    className="w-full text-left px-4 py-3 rounded-xl border border-outline-variant/20 text-on-surface text-sm font-medium hover:bg-primary-container/30 hover:border-primary/30 transition-all cursor-pointer disabled:opacity-50 flex items-center justify-between gap-2"
                  >
                    <div className="flex items-center gap-3">
                      <span className="material-symbols-outlined text-primary text-lg">
                        campaign
                      </span>
                      <span className="truncate">{c.name}</span>
                    </div>
                    {assigningId === c.id && (
                      <span className="material-symbols-outlined animate-spin text-primary text-xl shrink-0">
                        progress_activity
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
